// Recipe book v2 (M24). PURE (ENG-1). Tracks which crafting recipes the player
// has unlocked (Minecraft unlocks a recipe when you obtain one of its
// ingredients), and ghost-fills a crafting grid from the player inventory when a
// recipe is picked in the book. Shaped/shapeless only — smelting has no grid.
import type { Registries } from '../registry/load';
import type { RecipeDef } from '../registry/schemas';
import { Inventory, emptyStack, isEmpty, makeStack } from './inventory';
import { insert } from './transaction';
import { ingredientMatches, trimPattern } from './crafting';

/** A recipe that occupies the crafting grid (not smelting/blasting/…). */
export function isCraftingRecipe(r: RecipeDef): boolean {
  return r.type === 'shaped' || r.type === 'shapeless';
}

/** Does this recipe's ingredient footprint actually fit a `width`×`width` grid?
 *  (A recipe book shown on the player's 2×2 inventory grid must exclude recipes
 *  that need the 3×3 table — `recipeGrid` would otherwise silently truncate them
 *  into a wrong/partial ghost-fill.) */
export function recipeFitsGrid(r: RecipeDef, width: number): boolean {
  if (r.type === 'shapeless') return r.ingredients.length <= width * width;
  if (r.type !== 'shaped') return false;
  const { w, h } = trimPattern(r.pattern);
  return w <= width && h <= width;
}

/** Rough "do I have enough right now" check for recipe-book highlighting: sums
 *  available count per distinct ingredient token against the player's inventory.
 *  A heuristic (like Minecraft's own book highlighting), not the crafting grid's
 *  exact matcher — a recipe mixing a tag and an overlapping exact-name ingredient
 *  could double-count, but that doesn't occur in this game's recipe data. */
export function canCraftNow(r: RecipeDef, player: Inventory, reg: Registries, width: number): boolean {
  const grid = recipeGrid(r, width);
  const needed = new Map<string, number>();
  for (const token of grid) if (token) needed.set(token, (needed.get(token) ?? 0) + 1);
  for (const [token, count] of needed) {
    let have = 0;
    for (let i = 0; i < player.size; i++) have += ingredientMatches(reg, token, player.get(i)) ? player.get(i).count : 0;
    if (have < count) return false;
  }
  return true;
}

/** The ingredient tokens (item names or `#tag`s) a crafting recipe consumes. */
export function craftingIngredients(r: RecipeDef): string[] {
  if (r.type === 'shaped') return Object.values(r.key);
  if (r.type === 'shapeless') return [...r.ingredients];
  return [];
}

/** The ingredient token per grid slot (row-major, width²) for ghost fill: a
 *  shaped recipe keeps its pattern (anchored top-left); a shapeless one fills the
 *  first N slots. Slots with no ingredient are null. */
export function recipeGrid(r: RecipeDef, width: number): (string | null)[] {
  const grid: (string | null)[] = Array(width * width).fill(null);
  if (r.type === 'shaped') {
    for (let row = 0; row < r.pattern.length && row < width; row++) {
      const line = r.pattern[row]!;
      for (let col = 0; col < line.length && col < width; col++) {
        const ch = line[col]!;
        if (ch !== ' ') grid[row * width + col] = r.key[ch] ?? null;
      }
    }
  } else if (r.type === 'shapeless') {
    for (let i = 0; i < r.ingredients.length && i < width * width; i++) grid[i] = r.ingredients[i]!;
  }
  return grid;
}

/** Ghost-fill: return whatever is in the grid to the player, then pull one of
 *  each ingredient from the player inventory into the grid. Returns true if every
 *  needed slot was filled (false when an ingredient was missing → partial). */
export function fillCraftGrid(
  r: RecipeDef,
  craft: Inventory,
  player: Inventory,
  reg: Registries,
  width: number,
): boolean {
  const range = [...Array(player.size).keys()];
  for (let i = 0; i < craft.size; i++) {
    const s = craft.get(i);
    if (!isEmpty(s)) { insert(player, s, range); craft.set(i, emptyStack()); }
  }
  const grid = recipeGrid(r, width);
  let full = true;
  for (let i = 0; i < grid.length; i++) {
    const token = grid[i];
    if (!token) continue;
    const slot = findIngredient(player, token, reg);
    if (slot < 0) { full = false; continue; }
    const s = player.get(slot);
    craft.set(i, makeStack(s.item, 1, s.components));
    player.set(slot, s.count <= 1 ? emptyStack() : { ...s, count: s.count - 1 });
  }
  return full;
}

/** First player slot holding an item that satisfies `token` (name or `#tag`). */
function findIngredient(player: Inventory, token: string, reg: Registries): number {
  for (let i = 0; i < player.size; i++) {
    if (ingredientMatches(reg, token, player.get(i))) return i;
  }
  return -1;
}

/** The set of crafting recipes the player has unlocked (persists via toJSON/load). */
export class RecipeBook {
  private unlocked = new Set<string>();

  get size(): number {
    return this.unlocked.size;
  }

  isUnlocked(name: string): boolean {
    return this.unlocked.has(name);
  }

  unlock(name: string): boolean {
    if (this.unlocked.has(name)) return false;
    this.unlocked.add(name);
    return true;
  }

  /**
   * Unlock every crafting recipe that uses `itemName` as an ingredient (MC's
   * "unlock on ingredient pickup"). `hasTag` resolves `#tag` ingredients against
   * the obtained item. Returns the newly-unlocked recipe names.
   */
  unlockByObtaining(
    itemName: string,
    recipes: readonly RecipeDef[],
    hasTag: (item: string, tag: string) => boolean,
  ): string[] {
    const added: string[] = [];
    for (const r of recipes) {
      if (!isCraftingRecipe(r) || this.unlocked.has(r.name)) continue;
      const uses = craftingIngredients(r).some((tok) =>
        tok.startsWith('#') ? hasTag(itemName, tok.slice(1)) : tok === itemName);
      if (uses) { this.unlocked.add(r.name); added.push(r.name); }
    }
    return added;
  }

  toJSON(): string[] {
    return [...this.unlocked];
  }

  load(save: readonly string[] | null | undefined): void {
    this.unlocked = new Set(save ?? []);
  }
}
